import { faCheck } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Description, Field, Checkbox as HeadlessCheckbox, Label } from '@headlessui/react';
import classnames from 'classnames';
import React, { forwardRef } from 'react';

import { useThemeClasses } from '@/lib/legacy-theme/theme-utils';

export interface CheckBoxProps {
  label?: string;
  helperText?: string;
  error?: string;
  success?: string;
  variant?: 'default' | 'error' | 'success';
  size?: 'sm' | 'md' | 'lg';
  checked?: boolean;
  onChange?: (checked: boolean) => void;
  disabled?: boolean;
  required?: boolean;
  indeterminate?: boolean;
  labelPosition?: 'left' | 'right';
}

export const CheckBox = forwardRef<HTMLInputElement, CheckBoxProps>(
  ({ label, helperText, error, success, variant = 'default', size = 'md', checked = false, onChange, disabled, required, indeterminate = false, labelPosition = 'right', ...props }, ref) => {
    // Base checkbox classes
    const baseCheckboxClasses =
      'relative flex shrink-0 cursor-pointer items-center justify-center rounded border transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50';

    // Theme-aware variant classes
    const variantCheckboxClasses = {
      default: 'border-border bg-surface hover:bg-surface-hover focus:ring-primary data-[checked]:bg-primary data-[checked]:border-primary',
      error: 'border-error bg-surface hover:bg-surface-hover focus:ring-error data-[checked]:bg-error data-[checked]:border-error',
      success: 'border-success bg-surface hover:bg-surface-hover focus:ring-success data-[checked]:bg-success data-[checked]:border-success',
    };

    // Size classes for checkbox
    const sizeCheckboxClasses = {
      sm: 'h-4 w-4',
      md: 'h-5 w-5',
      lg: 'h-6 w-6',
    };

    // Icon size classes
    const iconSizeClasses = {
      sm: 'w-3 h-3',
      md: 'w-4 h-4',
      lg: 'w-5 h-5',
    };

    // Determine the actual variant based on error/success props
    const actualVariant = error ? 'error' : success ? 'success' : variant;

    // Combine checkbox classes
    const checkboxClasses = useThemeClasses(classnames(baseCheckboxClasses, variantCheckboxClasses[actualVariant], sizeCheckboxClasses[size]));

    // Icon classes
    const iconClasses = useThemeClasses(classnames('text-white transition-opacity duration-200', iconSizeClasses[size], !checked && !indeterminate && 'opacity-0'));

    // Container classes
    const containerClasses = useThemeClasses('flex items-center gap-3');

    // Label classes
    const labelClasses = useThemeClasses(classnames('font-theme-normal text-theme-base text-text select-none cursor-pointer', disabled && 'opacity-50 cursor-not-allowed'));

    // Helper text classes
    const helperTextClasses = useThemeClasses(classnames('mt-theme-xs text-theme-xs', error ? 'text-error' : success ? 'text-success' : 'text-text-muted'));

    const checkboxElement = (
      <HeadlessCheckbox ref={ref} checked={indeterminate ? false : checked} onChange={onChange} disabled={disabled} className={checkboxClasses} {...props}>
        <FontAwesomeIcon icon={faCheck} className={iconClasses} />
      </HeadlessCheckbox>
    );

    const labelElement = label && (
      <Label className={labelClasses}>
        {label}
        {required && <span className="text-error ml-1">*</span>}
      </Label>
    );

    return (
      <Field className={containerClasses}>
        {labelPosition === 'left' && labelElement}
        {checkboxElement}
        {labelPosition === 'right' && labelElement}

        {(helperText || error || success) && (
          <div className="w-full">
            <Description className={helperTextClasses}>{error || success || helperText}</Description>
          </div>
        )}
      </Field>
    );
  },
);

CheckBox.displayName = 'CheckBox';

export default CheckBox;
